import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  useMarkAllNotificationsAsRead,
  useMarkNotificationAsRead,
  useNotifications,
} from '@/hooks/use-notifications';
import { cn } from '@/lib/utils';
import type { Notification, NotificationSeverity } from '@disparos/contracts';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { AlertCircle, AlertTriangle, Bell, CheckCheck, Info } from 'lucide-react';
import { useState } from 'react';

const SEVERITY_ICON: Record<NotificationSeverity, typeof Info> = {
  info: Info,
  warning: AlertTriangle,
  critical: AlertCircle,
};

const SEVERITY_COLOR: Record<NotificationSeverity, string> = {
  info: 'text-muted-foreground',
  warning: 'text-amber-600 dark:text-amber-500',
  critical: 'text-destructive',
};

/**
 * Sino de notifications no Topbar. Badge mostra count não-lidas (max "9+").
 * Click abre dropdown com últimas 50 notifications. Click numa notif marca
 * como lida automaticamente. Botão "Marcar todas como lidas" no header.
 *
 * Polling 30s via `useNotifications` — sem Socket.IO no MVP.
 */
export function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useNotifications();
  const markRead = useMarkNotificationAsRead();
  const markAll = useMarkAllNotificationsAsRead();

  const items = data?.items ?? [];
  const unreadCount = data?.unreadCount ?? 0;
  const showBadge = unreadCount > 0;
  const badgeText = unreadCount > 9 ? '9+' : String(unreadCount);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative h-8 w-8" aria-label="Notificações">
          <Bell className="h-4 w-4" />
          {showBadge ? (
            <span
              className={cn(
                'absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[9px] font-semibold tabular-nums leading-none',
                unreadCount > 0 && 'bg-destructive text-destructive-foreground',
              )}
            >
              {badgeText}
            </span>
          ) : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span className="text-sm font-medium">Notificações</span>
          {unreadCount > 0 ? (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 gap-1 px-2 text-xs"
              onClick={() => void markAll.mutateAsync()}
              disabled={markAll.isPending}
            >
              <CheckCheck className="h-3 w-3" />
              Marcar todas
            </Button>
          ) : null}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        <div className="max-h-[28rem] overflow-y-auto">
          {isLoading ? (
            <div className="px-3 py-6 text-center text-xs text-muted-foreground">Carregando…</div>
          ) : items.length === 0 ? (
            <div className="px-3 py-8 text-center text-xs text-muted-foreground">
              Sem notificações ainda. Eventos importantes aparecem aqui.
            </div>
          ) : (
            items.map((n) => (
              <NotificationRow
                key={n.id}
                notification={n}
                onClick={() => {
                  if (n.readAt === null) {
                    void markRead.mutateAsync(n.id);
                  }
                }}
              />
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function NotificationRow({
  notification,
  onClick,
}: {
  notification: Notification;
  onClick: () => void;
}) {
  const Icon = SEVERITY_ICON[notification.severity];
  const iconColor = SEVERITY_COLOR[notification.severity];
  const isUnread = notification.readAt === null;

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full items-start gap-3 px-3 py-2.5 text-left transition-colors hover:bg-accent',
        isUnread && 'bg-primary/5',
      )}
    >
      <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', iconColor)} />
      <div className="min-w-0 flex-1 space-y-0.5">
        <div className="flex items-center gap-2">
          <p className={cn('truncate text-sm', isUnread ? 'font-medium' : 'font-normal')}>
            {notification.title}
          </p>
          {isUnread ? (
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-label="Não lida" />
          ) : null}
        </div>
        <p className="text-[11px] text-muted-foreground">
          {formatDistanceToNow(new Date(notification.createdAt), {
            locale: ptBR,
            addSuffix: true,
          })}
        </p>
      </div>
    </button>
  );
}
